import type { StateCreator } from 'zustand';
import type { Notification, NotificationType } from '../../types/notification.types';

const MAX_NOTIFICATIONS = 100;
const AUTO_CLEANUP_DAYS = 7;

interface NotificationSettings {
  disabledTypes: NotificationType[];
  showInPopover: boolean;
}

/**
 * Notification Slice - управление уведомлениями
 */
export interface NotificationSlice {
  notifications: Notification[];
  settings: NotificationSettings;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  clearAll: () => void;
  clearOld: () => void;
  getUnreadCount: () => number;
  getByType: (type: NotificationType) => Notification[];
  toggleNotificationType: (type: NotificationType) => void;
  isTypeEnabled: (type: NotificationType) => boolean;
  setShowInPopover: (show: boolean) => void;
}

const cleanupOldNotifications = (notifications: Notification[]): Notification[] => {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - AUTO_CLEANUP_DAYS);
  
  return notifications.filter(n => new Date(n.timestamp) > cutoffDate);
};

const limitNotifications = (notifications: Notification[]): Notification[] => {
  if (notifications.length <= MAX_NOTIFICATIONS) {
    return notifications;
  }
  
  return notifications
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, MAX_NOTIFICATIONS);
};

/**
 * Создание Notification Slice с использованием StateCreator
 */
export const createNotificationSlice: StateCreator<
  NotificationSlice,
  [],
  [],
  NotificationSlice
> = (set, get) => ({
  notifications: [],
  settings: {
    disabledTypes: [],
    showInPopover: true,
  },

  addNotification: (notification) => {
    set((state) => {
      const now = new Date();
      const recentDuplicate = state.notifications.find((n) => {
        const timeDiff = now.getTime() - new Date(n.timestamp).getTime();
        return (
          timeDiff < 3000 && 
          n.type === notification.type &&
          n.title === notification.title &&
          n.message === notification.message
        );
      });

      if (recentDuplicate) {
        if (import.meta.env.DEV) {
          console.log('🚫 Duplicate notification ignored:', notification.title);
        }
        return state; 
      }

      const newNotification: Notification = {
        ...notification,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
        read: false,
      };

      let updated = [newNotification, ...state.notifications];
      updated = cleanupOldNotifications(updated);
      updated = limitNotifications(updated);
      return { notifications: updated };
    });
  },

  markAsRead: (id) => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      ),
    }));
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
    }));
  },

  deleteNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearAll: () => {
    set({ notifications: [] });
  },

  clearOld: () => {
    set((state) => ({
      notifications: cleanupOldNotifications(state.notifications),
    }));
  },

  getUnreadCount: () => {
    const { notifications, settings } = get();
    return notifications.filter(
      (n) => !n.read && !settings.disabledTypes.includes(n.type)
    ).length;
  },

  getByType: (type) => {
    return get().notifications.filter((n) => n.type === type);
  },

  toggleNotificationType: (type) => {
    set((state) => {
      const disabledTypes = state.settings.disabledTypes.includes(type)
        ? state.settings.disabledTypes.filter((t) => t !== type)
        : [...state.settings.disabledTypes, type];
      
      return {
        settings: {
          ...state.settings,
          disabledTypes,
        },
      };
    });
  },

  isTypeEnabled: (type) => {
    return !get().settings.disabledTypes.includes(type);
  },

  setShowInPopover: (show) => {
    set((state) => ({
      settings: {
        ...state.settings,
        showInPopover: show,
      },
    }));
  },
});


